
import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { streamAllSearches } from '@/services/inventoryService';

interface AlertHistory {
  sentAt: string;
  searchTerm: string;
  itemsFound: number;
}

interface UserAlert {
  id: string;
  searchTerm: string;
  createdAt: string;
  lastRun: string | null;
  nextRun: string | null;
  results: any[] | null;
  history: AlertHistory[];
}

interface SearchState {
  [alertId: string]: {
    status: 'not-started' | 'in-progress' | 'complete' | 'empty-result';
    results: any[] | null;
    lastRun: string | null;
    nextRun: string | null;
    itemsAwaitingStoreData: Set<string>;
  };
}

interface SearchContextValue {
  searchState: SearchState;
  refreshingState: {[key: string]: boolean};
  startSearchForAlerts: (alerts: UserAlert[]) => Promise<void>;
  startSearchForAlert: (alert: UserAlert) => Promise<void>;
  clearSearchState: () => void;
  updateAlertResults: (alertId: string, results: any[], lastRun?: string, nextRun?: string) => void;
  setItemsAwaitingStoreData: (alertId: string, items: Set<string>) => void;
}

const SearchContext = createContext<SearchContextValue | undefined>(undefined);

export const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearchContext must be used within a SearchProvider');
  }
  return context;
};

const getNextScheduledTime = () => {
  const now = new Date();
  const next = new Date(now);
  
  // Round up to next 15 minute interval
  next.setMinutes(Math.ceil(next.getMinutes() / 15) * 15);
  next.setSeconds(0);
  next.setMilliseconds(0);
  
  // Check if we're within the 10am-9pm MST time window
  const hour = next.getHours();
  if (hour < 10) {
    next.setHours(10);
    next.setMinutes(0);
  } else if (hour >= 21) {
    next.setDate(next.getDate() + 1);
    next.setHours(10);
    next.setMinutes(0);
  }
  
  // Check if it's Sunday (0), set to Monday
  if (next.getDay() === 0) {
    next.setDate(next.getDate() + 1);
  }
  
  return next.toISOString();
};

export const SearchProvider = ({ children }: { children: ReactNode }) => {
  const [searchState, setSearchState] = useState<SearchState>({});
  const [refreshingState, setRefreshingState] = useState<{[key: string]: boolean}>({});

  const updateAlertResults = useCallback((alertId: string, results: any[], lastRun?: string, nextRun?: string) => {
    console.log(`📥 [SEARCH_CONTEXT] Update for alert ${alertId}: ${results.length} items`);
    setSearchState(prev => ({
      ...prev,
      [alertId]: {
        ...prev[alertId],
        results,
        lastRun: lastRun || new Date().toISOString(),
        nextRun: nextRun || getNextScheduledTime(),
        status: 'in-progress'
      }
    }));
  }, []);

  const setItemsAwaitingStoreData = useCallback((alertId: string, items: Set<string>) => {
    setSearchState(prev => ({
      ...prev,
      [alertId]: {
        ...prev[alertId],
        itemsAwaitingStoreData: items
      }
    }));
  }, []);

  const startSearchForAlert = useCallback(async (alert: UserAlert) => {
    console.log(`🔍 [SEARCH_CONTEXT] Starting search for alert ${alert.id}: "${alert.searchTerm}"`);
    
    // Initialize search state
    setSearchState(prev => ({
      ...prev,
      [alert.id]: {
        status: 'in-progress',
        results: prev[alert.id]?.results || null,
        lastRun: prev[alert.id]?.lastRun || null,
        nextRun: prev[alert.id]?.nextRun || null,
        itemsAwaitingStoreData: new Set()
      }
    }));

    setRefreshingState(prev => ({ ...prev, [alert.id]: true }));

    try {
      await streamAllSearches(
        [alert.searchTerm],
        // On each update
        (index, result) => {
          console.log(`📥 [SEARCH_CONTEXT] Update for "${alert.searchTerm}": ${result.items.length} items`);
          
          // Track which items are still awaiting store data
          const newItemsAwaitingStoreData = new Set<string>();
          
          result.items.forEach(item => {
            const hasStoreData = item.stores && 
                                item.stores.length > 0 && 
                                !(item.stores.length === 1 && item.stores[0].storeNumber === "No data available in table");
            
            if (!hasStoreData && item.storeQty === 0) {
              newItemsAwaitingStoreData.add(item.id);
            }
          });
          
          setItemsAwaitingStoreData(alert.id, newItemsAwaitingStoreData);
          updateAlertResults(alert.id, result.items);
        },
        // On search complete
        async (finalResults) => {
          console.log(`✅ [SEARCH_CONTEXT] Complete for "${alert.searchTerm}": ${finalResults[0]?.items.length || 0} final items`);
          
          const finalItemCount = finalResults[0]?.items.length || 0;
          const finalStatus = finalItemCount > 0 ? 'complete' : 'empty-result';
          
          console.log(`📊 [SEARCH_CONTEXT] Setting status for alert ${alert.id} to: ${finalStatus} (${finalItemCount} items)`);
          
          setSearchState(prev => ({
            ...prev,
            [alert.id]: {
              ...prev[alert.id],
              status: finalStatus,
              itemsAwaitingStoreData: new Set()
            }
          }));
        }
      );
    } catch (error) {
      console.error(`🔥 [SEARCH_CONTEXT] Error for alert ${alert.id} ("${alert.searchTerm}"):`, error);
      setSearchState(prev => ({
        ...prev,
        [alert.id]: {
          ...prev[alert.id],
          status: 'empty-result'
        }
      }));
    } finally {
      setRefreshingState(prev => ({ ...prev, [alert.id]: false }));
    }
  }, [updateAlertResults, setItemsAwaitingStoreData]);

  const startSearchForAlerts = useCallback(async (alerts: UserAlert[]) => {
    console.log(`🔍 [SEARCH_CONTEXT] Starting search for ${alerts.length} alerts`);
    
    for (const alert of alerts) {
      // Only start search if not already in progress or if it's an empty result (should retry)
      const currentState = searchState[alert.id];
      if (!currentState || currentState.status === 'not-started' || currentState.status === 'empty-result') {
        console.log(`🆕 [SEARCH_CONTEXT] Starting new search for alert ${alert.id} (status: ${currentState?.status || 'not-started'})`);
        await startSearchForAlert(alert);
      } else {
        console.log(`⏭️ [SEARCH_CONTEXT] Alert ${alert.id} already ${currentState.status}, skipping`);
      }
    }
  }, [searchState, startSearchForAlert]);

  const clearSearchState = useCallback(() => {
    setSearchState({});
    setRefreshingState({});
  }, []);

  return (
    <SearchContext.Provider value={{
      searchState,
      refreshingState,
      startSearchForAlerts,
      startSearchForAlert,
      clearSearchState,
      updateAlertResults,
      setItemsAwaitingStoreData
    }}>
      {children}
    </SearchContext.Provider>
  );
};
